// QA-136 (Kelly) — mobile-web View-Wheel config + floating-control visibility.
//
// Two Kelly-reported bugs on the mobile View-Wheel are encoded here as pure,
// testable rules so the JSX stays declarative:
//
// 1) A floating label-toggle control (Styled.ToggleLabelIconContainer, an
//    absolutely-positioned FAB) kept rendering OVER an opened segment panel,
//    obscuring the panel's caption text. It was hidden while the segment LIST
//    was open but NOT while a single segment panel was open. shouldShowLabelToggle
//    now hides it whenever a segment panel is open (selectedArc set) as well.
//
// 2) Segment name pills were clipped to "..." by default because the mobile
//    surface asked createPie for a segmentLableLimit of 10 chars, while the
//    hover/reveal path shows up to 25. MOBILE_VIEW_SEGMENT_LABEL_LIMIT raises
//    the default to match the reveal (25) so names are readable at a glance.

// Readable default character limit for the mobile View-Wheel rim-pill labels.
// Kept in agreement with createPie's highlight/reveal path (25 chars) so the
// default pill shows the same text the reveal would.
export const MOBILE_VIEW_SEGMENT_LABEL_LIMIT = 25;

/**
 * Whether the floating "toggle labels" control should be shown.
 * Hidden while either the segment LIST or a single segment PANEL is open, so it
 * can never overlap opened-segment content.
 * @param {{ openSegmentList?: boolean, selectedArc?: any }} state
 * @returns {boolean}
 */
export function shouldShowLabelToggle({ openSegmentList, selectedArc } = {}) {
  return !isSegmentSurfaceOpen({ openSegmentList, selectedArc });
}

/**
 * T22.225 (UX-25, MEASURED web-stg main.dd06f65e, /wheel/508170, 390x844):
 * with a single segment open (selectedArc) the fixed "Get the app" pill and
 * the Help FAB sat over the segment's text and images (111/111 sample points
 * under the pill were segment content). The Help FAB's hide signal
 * (setSegmentPanelOpen) was keyed on the segment LIST only, and the pill had
 * no gate at all. Same rule as the label toggle above: any open segment
 * surface (the list or a single segment) hides the floating controls.
 * @param {{ openSegmentList?: boolean, selectedArc?: any }} state
 * @returns {boolean}
 */
export function isSegmentSurfaceOpen({ openSegmentList, selectedArc } = {}) {
  return !!openSegmentList || !!selectedArc;
}

// T22.631 (Gordon 2026-10-21, MEASURED web-dev main.a0b1030e, /wheel/4312,
// 384x854): with NO segment open the wrapper still scrolls by its 96px
// Help-FAB bottom padding, and the old scroll handler faded the wheel to
// opacity 0 whenever the wrapper sat within 80px of its bottom — a 20px
// thumb scroll made the whole wheel vanish (5/8 scroll steps hidden). The
// fade is VW-2's "segment fills the screen" collapse, so it only applies
// while a single segment is open below the wheel.
export const WHEEL_COLLAPSE_BOTTOM_PX = 80;

/**
 * Wheel opacity for a given wrapper scroll position.
 * @param {{ scrollTop: number, scrollHeight: number, clientHeight: number,
 *   segmentOpen: boolean }} s
 * @returns {0|1}
 */
export function wheelOpacityForScroll({
  scrollTop,
  scrollHeight,
  clientHeight,
  segmentOpen,
} = {}) {
  if (!segmentOpen) return 1;
  const fromBottom = scrollHeight - scrollTop - clientHeight;
  return scrollHeight > clientHeight && fromBottom < WHEEL_COLLAPSE_BOTTOM_PX
    ? 0
    : 1;
}

// T22.631: while a segment (or the list) is open the Help FAB and the
// "Get the app" pill are hidden (T22.225), so the bottom room reserved for
// them is just empty space under the open segment (MEASURED 113px of
// wallpaper under the panel at 384x854). Reserve it only while they show.
export function reserveFloatingControlSpace({ openSegmentList, selectedArc } = {}) {
  return !isSegmentSurfaceOpen({ openSegmentList, selectedArc });
}

// T22.631: the sticky title bar (z-index 200) stayed pinned at the top while
// the open segment's Prev / "Back to wheel" / Next row (z-index 300) scrolled
// up over it — the tag and arrows sat on the wheel title. While a single
// segment is open the title scrolls away with the wheel instead.
export function pinWheelTitleBar({ selectedArc } = {}) {
  return !selectedArc;
}

/**
 * T22.630 (Gordon/Kelly, Galaxy S23+ video of /wheel/510012, MEASURED web-stg
 * main.b86c7763, 384x832): with NO segment open, scrolling the View Wheel page
 * down made the whole wheel vanish (WheelView opacity 1 -> 0 after a 96px
 * scroll), leaving only the wallpaper video's play button. The scroll handler
 * faded the wheel whenever the wrapper sat within 80px of its bottom — a rule
 * meant only for VW-2's open-segment collapse (scroll the wheel away so the
 * segment fills the screen). On a phone the plain wheel page itself only
 * scrolls ~96px, so ANY downward scroll hit that band. The fade now also
 * requires a single segment to be open (the list renders INSIDE WheelView, so
 * it must never fade either).
 * @param {{ scrollTop: number, scrollHeight: number, clientHeight: number,
 *   openSegmentList?: boolean, selectedArc?: any }} state
 * @returns {boolean}
 */
export function shouldCollapseWheelOnScroll({
  scrollTop,
  scrollHeight,
  clientHeight,
  openSegmentList,
  selectedArc,
} = {}) {
  if (!selectedArc || openSegmentList) return false;
  const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
  return scrollHeight > clientHeight && distanceFromBottom < 80;
}

// T22.123 (audit-10120531, MEASURED web-stg main.128d9358.js, /wheel/507968,
// 12 segments, 390x844, Chromium + WebKit): the Segment 12 rim pill sat under
// the icon toolbar (6/9 sample points covered) and the Toggle Labels icon sat
// on Segment 3 (3/9). Two causes on this page:
//   1) WheelView only reserved a fixed 5vh above the wheel box, but the
//      renderer-v2 engine pushes a crowded noon pill out by whole radius
//      tiers (assignRimPillRadiusTiers / phaseNoonPillOutward, geometry.js),
//      so on 507968 the Segment 12 pill rose 65px above the wheel box —
//      past the top of ContentView, under the z-index:100 Header.
//   2) the toggle icon was pinned at top:12svh, i.e. inside the right-hand
//      2-3 o'clock pill band.
// The icon now sits at the top of ContentView (LABEL_TOGGLE_TOP_PX), and
// useRimPillClearance pads WheelView just enough that no MEASURED pill sits
// above ContentView's top or under the icon. A keep-out only pushes the pills
// that share its columns, so a lane-wide reserve never shoves the bottom
// pills down under the fixed "Get the app" pill.

// Top offset (px) of the label-toggle icon inside ContentView.
export const LABEL_TOGGLE_TOP_PX = 8;

// Gap (px) kept between a rim pill and a keep-out edge above it.
export const RIM_PILL_TOP_GAP_PX = 6;

/**
 * Top padding (px) WheelView needs so no rim pill sits above the bottom edge
 * of a keep-out box it shares columns with (ContentView's top edge spans the
 * whole width; the toggle icon only its own columns).
 * Shift-invariant: `pills` are measured with `currentPadding` already
 * applied, so the result does not feed back on itself.
 * @param {{
 *   pills: Array<{top:number,left:number,right:number}>,
 *   currentPadding?: number,
 *   keepOuts?: Array<{bottom:number,left?:number,right?:number}>,
 *   gap?: number,
 * }} p  keepOut left/right omitted = full width
 * @returns {number} whole px, 0 when no pill is measured or none is covered
 */
export function rimPillClearancePadding({
  pills = [],
  currentPadding = 0,
  keepOuts = [],
  gap = RIM_PILL_TOP_GAP_PX,
} = {}) {
  let need = 0;
  for (const pill of pills) {
    if (!pill || !Number.isFinite(pill.top)) continue;
    const unpaddedTop = pill.top - (currentPadding || 0);
    for (const k of keepOuts) {
      if (!k || !Number.isFinite(k.bottom)) continue;
      const sharesColumns =
        !(Number.isFinite(k.left) && Number.isFinite(k.right)) ||
        (pill.left < k.right && pill.right > k.left);
      if (sharesColumns) need = Math.max(need, k.bottom + gap - unpaddedTop);
    }
  }
  return Math.max(0, Math.ceil(need));
}

// T20.39 (Kelly, "Fix bubble location for segment labels"; MEASURED web-stg
// main.1436082b.js, /wheel/507342 "Green Bay Lake", 33 segments, 390x844):
// the circular pill fan (T22.295) is 492px tall (pills 325..817) but the
// room between the author card (bottom 398) and the fixed "Get the app"
// pill (top 783) is 385px. The T22.123 clearance only kept pills below
// ContentView's top (152), so "Segment 32" / "Segment 1" / "lake view" sat
// on the card's Followers/Following row, and Segments 15-17 sat under the
// Get-the-app pill and past the screen bottom with no way to scroll to
// them (the fan paints outside WheelView's box, so it never added height).
// Two rules close that:
//   1) the in-flow content above WheelView (description + author card) is
//      a full-width keep-out, like ContentView's top (inFlowContentBottom);
//   2) when a pill at rest sits under the fixed pill (or past the screen
//      bottom), WheelView reserves the fan's overhang + the pill's
//      clearance below itself, so the page scrolls the last pills clear of
//      the button (rimPillBottomReserve).
// A wheel whose fan already fits (e.g. 12-14 segments) gets neither.

/**
 * Bottom edge (px) of the in-flow content that precedes WheelView inside
 * ContentView. Absolutely / fixed positioned siblings (the label toggle,
 * the notification bar) are skipped: they float, they don't take room.
 * @param {Element} host  WheelView
 * @param {{ skip?: Element|null, win?: Window }} [opts]
 * @returns {number} -Infinity when nothing in flow precedes WheelView
 */
export function inFlowContentBottom(host, { skip = null, win } = {}) {
  let bottom = -Infinity;
  const view = win || (host && host.ownerDocument && host.ownerDocument.defaultView);
  for (let el = host && host.previousElementSibling; el; el = el.previousElementSibling) {
    if (el === skip) continue;
    const pos = view && view.getComputedStyle ? view.getComputedStyle(el).position : "";
    if (pos === "absolute" || pos === "fixed") continue;
    const r = el.getBoundingClientRect();
    if (r && r.height > 0 && Number.isFinite(r.bottom)) bottom = Math.max(bottom, r.bottom);
  }
  return bottom;
}

/**
 * Bottom padding (px) WheelView needs so its rim pills can be scrolled clear
 * of the fixed "Get the app" pill. 0 when every pill already clears it (and
 * the screen bottom) at rest — the common case, so nothing changes there.
 * `pills` were measured BEFORE the top-padding change `shiftPx` was applied;
 * `hostBottom` is read AFTER it, so each pill is moved by `shiftPx` before
 * it is compared. Scroll moves pills and WheelView alike, so the overhang
 * does not depend on it; only the "is it covered at rest" check adds
 * `scrolledPx` (how far the page is scrolled) back.
 * @param {{
 *   pills: Array<{bottom:number,left:number,right:number}>,
 *   hostBottom: number,          WheelView rect bottom now (incl. currentReserve)
 *   currentReserve?: number,
 *   shiftPx?: number,
 *   scrolledPx?: number,
 *   obstacle?: {top:number,left:number,right:number}|null,
 *   viewportBottom?: number,
 *   gap?: number,
 *   clearance?: number,
 * }} p
 * @returns {number} whole px
 */
export function rimPillBottomReserve({
  pills = [],
  hostBottom,
  currentReserve = 0,
  shiftPx = 0,
  scrolledPx = 0,
  obstacle = null,
  viewportBottom = Infinity,
  gap = RIM_PILL_TOP_GAP_PX,
  clearance = 0,
} = {}) {
  if (!Number.isFinite(hostBottom)) return 0;
  let covered = false;
  let maxBottom = -Infinity;
  for (const pill of pills) {
    if (!pill || !Number.isFinite(pill.bottom)) continue;
    maxBottom = Math.max(maxBottom, pill.bottom);
    const restBottom = pill.bottom + (shiftPx || 0) + (scrolledPx || 0);
    if (restBottom > viewportBottom - gap) covered = true;
    if (
      obstacle &&
      Number.isFinite(obstacle.top) &&
      pill.left < obstacle.right &&
      pill.right > obstacle.left &&
      restBottom > obstacle.top - gap
    ) {
      covered = true;
    }
  }
  if (!covered) return 0;
  const overhang = maxBottom + (shiftPx || 0) - (hostBottom - (currentReserve || 0));
  return Math.max(0, Math.ceil(overhang + clearance));
}

// T22.369 (Production check after the T22.278 hotfix, 2026-10-17; REPRODUCED
// web-stg main.4d27e002.js, replica of /wheel/424673 "Essential Oils & the
// Brain App" (6 segments), 390x664, logged in): rimPillBottomReserve only
// lets the visitor scroll the bottom pills clear of the fixed "Get the app"
// pill — at rest "Emotions" and "Essential Oils..." still sat under it
// (pill bottom 627, button top 603). The floating pill now steps aside
// (GetAppFab [data-over-rim-pills]) while it covers a visible rim pill at
// the current scroll position, and comes back as soon as it doesn't.
export const GET_APP_OVER_RIM_PILLS_ATTR = "data-over-rim-pills";

/**
 * Whether the fixed "Get the app" pill covers (or crowds within `gap`) any
 * visible rim pill at the current scroll position.
 * @param {{
 *   pills: Array<{top:number,bottom:number,left:number,right:number}>,
 *   button?: {top:number,bottom:number,left:number,right:number,height?:number}|null,
 *   gap?: number,
 * }} p
 * @returns {boolean}
 */
export function getAppButtonCoversRimPill({
  pills = [],
  button = null,
  gap = RIM_PILL_TOP_GAP_PX,
} = {}) {
  if (!button || !(button.bottom > button.top)) return false;
  return pills.some(
    (pill) =>
      !!pill &&
      pill.left < button.right &&
      pill.right > button.left &&
      pill.bottom > button.top - gap &&
      pill.top < button.bottom
  );
}
